import {
  TGetLearningProgressListResDto,
} from "@/api/services/main/learning-progress/dtos/get-learning-progress-list.dto";
import {
  TGetExamListByIndustryReqDto,
  TGetExamListByIndustryResDto,
} from "@/api/services/main/learning-progress/dtos/exam/get-exam-list-by-industry.dto";
import {
  TGetExamDetailByIndustryReqDto,
  TGetExamDetailByIndustryResDto,
} from "@/api/services/main/learning-progress/dtos/exam/get-exam-detail-by-industry.dto";
import {
  TGetLearningProgressLearnersReqDto,
  TGetLearningProgressLearnersResDto,
} from "@/api/services/main/learning-progress/dtos/get-learning-progress-learners.dto";
import {
  TGetLearningProgressLearnerDetailReqDto,
  TGetLearningProgressLearnerDetailResDto,
} from "@/api/services/main/learning-progress/dtos/get-learning-progress-learner-detail.dto";
import {
  TCreateLearnerCommentReqDto,
  TCreateLearnerCommentResDto,
  TGetLearnerCommentsReqDto,
  TGetLearnerCommentsResDto,
  TUpdateLearnerCommentReqDto,
  TUpdateLearnerCommentResDto,
} from "@/api/services/main/learning-progress/dtos/learner-comment.dto";
import {
  TGetLearnerProgressReqDto,
  TGetLearnerProgressResDto,
} from "@/api/services/main/learning-progress/dtos/get-learner-progress.dto";
import {
  TGetLearnerCurriculumProgressReqDto,
  TGetLearnerCurriculumProgressResDto,
} from "@/api/services/main/learning-progress/dtos/get-learner-curriculum-progress.dto";
import {
  TUpdateLearnerNotesReqDto,
  TUpdateLearnerNotesResDto,
} from "@/api/services/main/learning-progress/dtos/update-learner-notes.dto";
import {
  TGetCompaniesForReportReqDto,
  TGetCompaniesForReportResDto,
} from "@/api/services/main/learning-progress/dtos/get-companies-for-report.dto";
import {
  TGetLearnersForReportReqDto,
  TGetLearnersForReportResDto,
} from "@/api/services/main/learning-progress/dtos/get-learners-for-report.dto";
import { TExportLearnersReportResDto } from "@/api/services/main/learning-progress/dtos/export-report.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { MASTER_DATA_CACHE_TIME } from "@/constants/common";
import { clientApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

export const getLearningProgressIndustriesApi = async (): Promise<TGetLearningProgressListResDto> => {
  const { data } = await clientApi.get<TGetLearningProgressListResDto>("/learning-progress/industries");
  return data;
};

export const useLearningProgressIndustries = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_INDUSTRIES],
    queryFn: () => getLearningProgressIndustriesApi(),
  });
};

export const getLearningProgressLearnersApi = async (params: TGetLearningProgressLearnersReqDto): Promise<TGetLearningProgressLearnersResDto> => {
  const { industryId, ...rest } = params;
  const { data } = await clientApi.get<TGetLearningProgressLearnersResDto>(`/learning-progress/industry/${industryId}/learners`, { params: rest });
  return data;
};

export const useLearningProgressLearners = (params: TGetLearningProgressLearnersReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNERS, params],
    queryFn: () => getLearningProgressLearnersApi(params),
    enabled: !!params.industryId,
  });
};

export const getCompaniesForReportApi = async (params: TGetCompaniesForReportReqDto): Promise<TGetCompaniesForReportResDto> => {
  const { industryId, ...rest } = params;
  const { data } = await clientApi.get<TGetCompaniesForReportResDto>(`/learning-progress/industry/${industryId}/companies`, { params: rest });
  return data;
};

export const useCompaniesForReport = (params: TGetCompaniesForReportReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_COMPANIES_FOR_REPORT, params],
    queryFn: () => getCompaniesForReportApi(params),
    enabled: !!params.industryId,
  });
};

export const getLearnersForReportApi = async (params: TGetLearnersForReportReqDto): Promise<TGetLearnersForReportResDto> => {
  const { industryId, companyId, ...rest } = params;
  const { data } = await clientApi.get<TGetLearnersForReportResDto>(
    `/learning-progress/industry/${industryId}/companies/${companyId}/learners-for-report`,
    { params: rest },
  );
  return data;
};

export const useLearnersForReport = (params: TGetLearnersForReportReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNERS_FOR_REPORT, params],
    queryFn: () => getLearnersForReportApi(params),
    enabled: !!params.industryId && !!params.companyId,
  });
};

export const exportLearnersReportApi = async (
  industryId: number,
  companyId: number,
  learnerIds: number[],
  periodFrom: string,
  periodTo: string,
): Promise<TExportLearnersReportResDto> => {
  const { data } = await clientApi.post<TExportLearnersReportResDto>(
    `/learning-progress/industry/${industryId}/companies/${companyId}/export-report`,
    { learnerIds, periodFrom, periodTo },
  );
  return data;
};

export const getLearningProgressExamListApi = async (params: TGetExamListByIndustryReqDto): Promise<TGetExamListByIndustryResDto> => {
  const { industryId, ...rest } = params;
  const { data } = await clientApi.get<TGetExamListByIndustryResDto>(`/learning-progress/industry/${industryId}/exams`, { params: rest });
  return data;
};

export const useLearningProgressExamList = (params: TGetExamListByIndustryReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_EXAM_LIST, params.industryId],
    queryFn: () => getLearningProgressExamListApi(params),
    enabled: !!params.industryId,
    // 試験一覧は分野ごとのマスタデータで編集中に変わらない。仮想スクロールでセルが再マウントする度に
    // 再取得して重くなるのを防ぐため、セッション中はキャッシュする (一覧画面でも有益)。
    staleTime: MASTER_DATA_CACHE_TIME,
    gcTime: MASTER_DATA_CACHE_TIME,
  });
};

export const getLearningProgressExamDetailApi = async (params: TGetExamDetailByIndustryReqDto): Promise<TGetExamDetailByIndustryResDto> => {
  const { industryId, examId, ...rest } = params;
  const { data } = await clientApi.get<TGetExamDetailByIndustryResDto>(`/learning-progress/industry/${industryId}/exams/${examId}`, { params: rest });
  return data;
};

export const useLearningProgressExamDetail = (params: Pick<TGetExamDetailByIndustryReqDto, 'industryId'> & { examId: number | null }) => {
  const examId = params.examId;
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_EXAM_DETAIL, params.industryId, examId],
    queryFn: () => getLearningProgressExamDetailApi({ industryId: params.industryId, examId: examId as number }),
    enabled: !!params.industryId && examId !== null && examId !== undefined,
  });
};

export const getLearningProgressLearnerDetailApi = async (
  params: TGetLearningProgressLearnerDetailReqDto,
): Promise<TGetLearningProgressLearnerDetailResDto> => {
  const { data } = await clientApi.get<TGetLearningProgressLearnerDetailResDto>(
    `/learning-progress/learner/${params.learnerId}`,
  );
  return data;
};

export const useLearningProgressLearnerDetail = (params: TGetLearningProgressLearnerDetailReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNER_DETAIL, params.learnerId],
    queryFn: () => getLearningProgressLearnerDetailApi(params),
    enabled: !!params.learnerId,
  });
};

export const getLearnerProgressApi = async (
  params: TGetLearnerProgressReqDto,
): Promise<TGetLearnerProgressResDto> => {
  const { data } = await clientApi.get<TGetLearnerProgressResDto>(
    `/learning-progress/learner/${params.learnerId}/progress`,
  );
  return data;
};

export const useLearnerProgress = (params: TGetLearnerProgressReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNER_PROGRESS, params.learnerId],
    queryFn: () => getLearnerProgressApi(params),
    enabled: !!params.learnerId,
  });
};

export const getLearnerCurriculumProgressApi = async (
  params: TGetLearnerCurriculumProgressReqDto,
): Promise<TGetLearnerCurriculumProgressResDto> => {
  const { data } = await clientApi.get<TGetLearnerCurriculumProgressResDto>(
    `/learning-progress/learner/${params.learnerId}/curriculum-progress`,
  );
  return data;
};

export const useLearnerCurriculumProgress = (params: TGetLearnerCurriculumProgressReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNER_CURRICULUM_PROGRESS, params.learnerId],
    queryFn: () => getLearnerCurriculumProgressApi(params),
    enabled: !!params.learnerId,
  });
};

export const getLearnerCommentsApi = async (
  params: TGetLearnerCommentsReqDto,
): Promise<TGetLearnerCommentsResDto> => {
  const { learnerId, ...rest } = params;
  const { data } = await clientApi.get<TGetLearnerCommentsResDto>(
    `/learning-progress/learner/${learnerId}/comments`,
    { params: rest },
  );
  return data;
};

export const createLearnerCommentApi = async (
  learnerId: number | string,
  payload: TCreateLearnerCommentReqDto,
): Promise<TCreateLearnerCommentResDto> => {
  const { data } = await clientApi.post<TCreateLearnerCommentResDto>(
    `/learning-progress/learner/${learnerId}/comments`,
    payload,
  );
  return data;
};

export const updateLearnerCommentApi = async (
  learnerId: number | string,
  commentId: number | string,
  payload: TUpdateLearnerCommentReqDto,
): Promise<TUpdateLearnerCommentResDto> => {
  const { data } = await clientApi.patch<TUpdateLearnerCommentResDto>(
    `/learning-progress/learner/${learnerId}/comments/${commentId}`,
    payload,
  );
  return data;
};

export const deleteLearnerCommentApi = async (
  learnerId: number | string,
  commentId: number | string,
): Promise<void> => {
  await clientApi.delete(`/learning-progress/learner/${learnerId}/comments/${commentId}`);
};

export const updateLearnerNotesApi = async (
  learnerId: number | string,
  payload: TUpdateLearnerNotesReqDto,
): Promise<TUpdateLearnerNotesResDto> => {
  const { data } = await clientApi.patch<TUpdateLearnerNotesResDto>(
    `/learning-progress/learner/${learnerId}/notes`,
    payload,
  );
  return data;
};
